/**
 * media 弹窗面板（特殊剧情三载体之一：encounter 偶遇 / message 人脉消息 / media 媒体弹窗）。
 *
 * media 载体呈现舆论向内容：
 *  - hotpost 热议讨论帖（观众自发讨论）→ 方案 A「素净纸卡」
 *  - weibo 营销号微博（娱乐新闻 + 热评，2026-10-17 新增，与 hotpost 的「观众自发帖」区分）→ 微博详情页
 *  - news 新闻快讯（行业媒体报道，无评论）→ 方案 B「图文卡」/ 方案 D「报刊」
 *
 * 版式分派逻辑（各版式卡内都不放关闭钮，2026-10-16 弹窗统一批：点卡外遮罩关闭）：
 *  - hotpost 走素净纸卡（楼主行 + 标题 + 正文 + 细虚线 + 热门回复）
 *  - weibo 走微博详情页（博主行 + 正文 + 配图位 + 互动栏 + 热门评论）
 *  - news 且 style!=='paper' 走图文卡（来源行 + 标题 + 配图位 + 正文 + 互动数）
 *  - news 且 style==='paper' 走报刊（报头双线 + 标签 + 标题 + 导语 + 署名）
 */
import { FixedMedia } from '../../data/types';
import { GameState } from '../../systems/GameState';
import { Utils } from '../../core/Utils';
import { bandOf } from '../../core/RepBands';
import { createDiv, createModalDismiss, createSpan, plotHtml, setPlotText } from '../ui';
import { icon, IconName } from '../icons';
import { avatarColor, createCommentFloor } from '../CommentFloor';
import {
    BUZZ_FALLBACK, commentCount, floorLikeCount, mediaSeed, repostCount, weiboLikeCount,
} from '../buzzReads';
import { fillAvatarSlot } from '../../avatar/gamemount';
import { prefetchMediaImage } from '../mediaPrefetch';

/**
 * **这一张卡的楼层评论**（2026-10-22 用户令「评论做差分」）：
 * `media.commentsByRep` 存在时按**当前口碑**取档（`bandOf`：取命中的最紧一档）；
 * 一档都没命中的 ⇒ 落回 `media.comments`。
 * 评论差分与触发概率取的是**同一套档**（都在 `core/RepBands.ts` 的 `bandOf` 里判），
 * 不会出现"概率按低档算、评论按高档显"的分叉。
 */
function commentsOf(media: FixedMedia): string[] {
    const bands = media.commentsByRep;
    if (!bands || bands.length === 0) return media.comments;
    const rep = GameState.getInstance().getAttribute('reputation');
    return bandOf(bands, rep)?.comments ?? media.comments;
}

/** 网名（当前一律 momo，等最后统一改池） */
const REVIEWER_DISPLAY_NAME = 'momo';

export class MediaPanel {
    readonly element: HTMLElement;
    /** 弹窗开合时通知宿主（悬浮入口要让位，与 VN 纸卡同一条规矩） */
    onOpenChanged?: (open: boolean) => void;
    /** 玩家点了「知道了」后的回调（宿主据此推进剧情队列） */
    onDone?: () => void;

    private isOpen = false;

    constructor() {
        const wrap = createDiv('modal-wrap');
        const card = createDiv('modal-note-card media-card');

        // 内容容器——每次 show() 重建
        const body = createDiv('media-body');
        card.appendChild(body);

        // 关闭热区＝遮罩本体（2026-10-16 弹窗统一批·茄子令：三张卡的「知道了」全删，改点空白关闭）。
        // 本面板原先连遮罩都没有（底下不糊、且挂在外壳上时点不到空白），这一颗同时把甲批要的淡底+模糊带上。
        wrap.appendChild(createModalDismiss(() => this.close()));
        wrap.appendChild(card);
        this.element = wrap;
        this.bodyEl = body;
    }

    private readonly bodyEl: HTMLElement;

    /** 展示一条 media 内容，渲染后弹出 */
    show(media: FixedMedia): void {
        prefetchMediaImage(media.image);   // 兜底：不经 PlotPlayer 的调用路径也提前拉配图（幂等）
        this.bodyEl.replaceChildren();
        if (media.kind === 'hotpost') {
            this.bodyEl.appendChild(this.renderHotpost(media));
        } else if (media.kind === 'weibo') {
            this.bodyEl.appendChild(this.renderWeibo(media));
        } else if (media.style === 'paper') {
            this.bodyEl.appendChild(this.renderNewsPaper(media));
        } else {
            this.bodyEl.appendChild(this.renderNewsCard(media));
        }
        // 卡里没有任何标题行（三种版式都把标题当正文第一块画），modal.ts 取不到可及名称，每条内容现声明。
        // 每次开卡都重设：卡是复用的，别让上一条的名字留在这一条上（2026-10-16 弹窗统一批）
        this.element.dataset.dialogLabel = media.title
            || (media.kind === 'hotpost' ? '热议帖' : media.kind === 'weibo' ? '微博' : '新闻快讯');
        this.open();
    }

    open(): void {
        if (this.isOpen) return;
        this.isOpen = true;
        this.element.classList.add('show');
        this.onOpenChanged?.(true);
    }

    close(): void {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.element.classList.remove('show');
        this.onOpenChanged?.(false);
        this.onDone?.();
    }

    // ===== hotpost 素净纸卡 =====

    private renderHotpost(media: FixedMedia): HTMLElement {
        const card = createDiv('media-hotpost');

        // 楼主行：头像 + 昵称 + 时间 + 楼主标签
        const poster = createDiv('hp-poster');
        const avatar = createDiv('hp-avatar');
        avatar.style.background = avatarColor(media.body);
        avatar.textContent = REVIEWER_DISPLAY_NAME.slice(0, 1);
        poster.appendChild(avatar);
        const posterInfo = createDiv('hp-poster-info');
        posterInfo.appendChild(createDiv('hp-poster-name', [REVIEWER_DISPLAY_NAME]));
        const p = GameState.getInstance().player;   // 渲染时现取单例（构造期捕获会在建角换实例后失效）
        const dateStr = `第${p.year + 1}年${p.month}月`;
        posterInfo.appendChild(createDiv('hp-poster-time', [dateStr]));
        poster.appendChild(posterInfo);
        const tag = createSpan('hp-poster-tag', '楼主');
        poster.appendChild(tag);
        card.appendChild(poster);

        // 帖子标题（微博无独立标题、不走这里；hotpost 必填）
        if (media.title) card.appendChild(setPlotText(createDiv('hp-title'), media.title));

        // 楼主正文
        card.appendChild(setPlotText(createDiv('hp-body'), media.body));

        // 细虚线
        card.appendChild(createDiv('hp-divider'));

        // 热门回复标题（无条数显示——2026-10-14 用户令）
        const sect = createDiv('hp-sect');
        const sectLabel = createSpan('hp-sect-label');
        sectLabel.appendChild(icon('chat' as IconName));
        sectLabel.appendChild(createSpan(undefined, '热门回复'));
        sect.appendChild(sectLabel);
        card.appendChild(sect);

        // 回应楼层：点赞按楼层逐层衰减（首楼最高，往下必然递减）。
        // 注意：整串楼层必须共用「这一帖」的种子（标题＋年月），别拿各楼正文当种子——
        //    那样每楼各起一条独立链，首楼与倍数全乱，实测会抖出 1925 掉到 462 这种掉出带宽的台阶（10-17 踩过）。
        const seed = mediaSeed(media.title ?? media.body);
        const comments = commentCount(media.buzz ?? BUZZ_FALLBACK, seed);
        commentsOf(media).forEach((comment, floor) => {
            card.appendChild(this.renderComment(comment, seed, comments, floor));
        });

        // 2026-10-16 弹窗统一批（茄子令）：卡内这颗「知道了」删掉，关闭热区＝遮罩本体（见构造函数）
        return card;
    }

    /** 评论楼层＝公共组件 CommentFloor（热议楼层与微博热评同构共用，2026-10-23 茄子令抽公共） */
    private renderComment(text: string, seed: string, comments: number, floor: number): HTMLElement {
        return createCommentFloor({
            name: REVIEWER_DISPLAY_NAME,
            text,
            avatarBg: avatarColor(text),
            likes: Utils.formatCountCN(floorLikeCount(comments, seed, floor)),
        });
    }

    // ===== weibo 微博详情页（营销号娱乐新闻 + 热评，2026-10-17 新增） =====

    /**
     * 微博形态：最上面一层是**营销号**发的娱乐新闻，下面挂热评。
     * 与 hotpost 的区分（用户 2026-10-17 口令）：hotpost＝观众自发的讨论帖（楼主开楼 + 标题 + 楼层），
     * weibo＝营销号发的娱乐新闻（博主行 + 正文带话题 + 互动栏），两者是不同的舆论场。
     * 结构照新浪微博详情页：博主行（头像 / 昵称 / 认证 / 时间来源 / 关注）→ 正文 → 配图 → 互动栏（转发·评论·点赞）→ 热门评论。
     */
    private renderWeibo(media: FixedMedia): HTMLElement {
        const card = createDiv('media-weibo');

        // 博主行：头像 + 昵称（+ 认证标）+ 时间·来源 + 关注钮
        const author = media.author ?? '娱圈观察';
        const p = GameState.getInstance().player;   // 渲染时现取单例（构造期捕获会在建角换实例后失效）
        const byline = createDiv('wb-byline');
        const avatar = createDiv('wb-avatar');
        if (media.authorAvatar === 'player') {
            // 后援会官方号：头像＝玩家本人捏脸（2026-10-23 用户令）。
            // 落色块兜底，等 PNG 渲完再换下（fillAvatarSlot 内部处理离档丢弃）。
            avatar.style.background = avatarColor(author + media.body);
            fillAvatarSlot(avatar, author.slice(0, 1), p.avatar);
        } else {
            avatar.style.background = avatarColor(author + media.body);
            avatar.textContent = author.slice(0, 1);
        }
        byline.appendChild(avatar);
        const info = createDiv('wb-info');
        const nameRow = createDiv('wb-name-row');
        nameRow.appendChild(createSpan('wb-name', author));
        nameRow.appendChild(createSpan('wb-verified', 'V'));   // 认证标（营销号；纯装饰）
        info.appendChild(nameRow);
        info.appendChild(createDiv('wb-meta', [`第${p.year + 1}年${p.month}月`]));
        byline.appendChild(info);
        byline.appendChild(createSpan('wb-follow', '关注'));   // 关注钮（只读展示，不作交互）
        card.appendChild(byline);

        // 正文（营销号文案，可带 #话题# 与 @本人；微博无独立标题）
        // 2026-10-30 茄子令：@玩家名 与话题同色高亮。只认当前玩家名（正文里 @ 后面没有分隔符，
        // 泛匹配会把紧随其后的汉字一起染绿，所以按已知名字精确替换）。
        const bodyEl = createDiv('wb-body');
        let bodyHtml = plotHtml(media.body);
        const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        for (const n of new Set([p.name, (p.nickname ?? '').trim()].filter(n => !!n))) {
            const safe = esc(n);
            bodyHtml = bodyHtml.split(`@${safe}`).join(`<span class="wb-topic">@${safe}</span>`);
        }
        bodyEl.innerHTML = bodyHtml.replace(/#([^#]+)#/g, '<span class="wb-topic">#$1#</span>');
        card.appendChild(bodyEl);

        // 配图（可选）
        if (media.image) {
            const img = document.createElement('img');
            img.className = 'wb-image';
            img.decoding = 'async';
            img.src = media.image;
            img.alt = '';
            img.onerror = () => { img.style.display = 'none'; };   // 文件缺失/名写错＝藏图，不显示破图
            card.appendChild(img);
        }

        // 互动栏：转发 / 评论 / 点赞（三颗均分，微博口径；数锚＝buzz，按内容做种）
        const seed = mediaSeed(author + media.body);
        const comments = commentCount(media.buzz ?? BUZZ_FALLBACK, seed);
        const reposts = repostCount(comments, seed);
        const likes = weiboLikeCount(comments, seed);
        const bar = createDiv('wb-bar');
        const repostItem = createSpan('wb-bar-item');
        repostItem.appendChild(icon('repost' as IconName));
        repostItem.appendChild(createSpan(undefined, ` ${Utils.formatCountCN(reposts)}`));
        bar.appendChild(repostItem);
        const chatItem = createSpan('wb-bar-item active');
        chatItem.appendChild(icon('chat' as IconName));
        chatItem.appendChild(createSpan(undefined, ` ${Utils.formatCountCN(comments)}`));
        bar.appendChild(chatItem);
        const likeItem = createSpan('wb-bar-item');
        likeItem.appendChild(icon('like' as IconName));
        likeItem.appendChild(createSpan(undefined, ` ${Utils.formatCountCN(likes)}`));
        bar.appendChild(likeItem);
        card.appendChild(bar);

        // 热门评论（无标题行——评论数选中态已暗示这是评论列表）
        commentsOf(media).forEach((cmt, floor) => {
            card.appendChild(this.renderWeiboComment(cmt, seed, comments, floor));
        });

        return card;
    }

    /** 微博热评一条＝公共组件 CommentFloor（与热议楼层同一条，2026-10-23 抽公共后无差异） */
    private renderWeiboComment(text: string, seed: string, comments: number, floor: number): HTMLElement {
        return this.renderComment(text, seed, comments, floor);
    }

    // ===== news 图文卡（方案 B） =====

    private renderNewsCard(media: FixedMedia): HTMLElement {
        const card = createDiv('media-news-card');

        // 来源行
        const src = createDiv('nw-src');
        src.appendChild(createSpan('nw-src-name', '星闻速递'));
        const p = GameState.getInstance().player;   // 渲染时现取单例（构造期捕获会在建角换实例后失效）
        const dateStr = `娱乐 · 第${p.year + 1}年${p.month}月`;
        src.appendChild(createSpan('nw-src-date', dateStr));
        card.appendChild(src);

        // 标题（带左竖条；微博无标题不走这里）
        if (media.title) card.appendChild(setPlotText(createDiv('m-title'), media.title));

        // 配图位（有 image 才放图，无配图不显示占位块——2026-10-14 用户令）
        if (media.image) {
            const img = document.createElement('img');
            img.className = 'nw-image';
            img.decoding = 'async';
            img.src = media.image;
            img.alt = '';
            img.onerror = () => { img.style.display = 'none'; };   // 文件缺失/名写错＝藏图，不显示破图
            card.appendChild(img);
        }

        // 正文
        card.appendChild(setPlotText(createDiv('nw-body'), media.body));

        // 底部互动数行：两颗都是「图标＋数」，不带文字（2026-10-17 茄子令「去掉讨论两个字」）
        const seed = mediaSeed(media.title ?? media.body);
        const comments = commentCount(media.buzz ?? BUZZ_FALLBACK, seed);
        const reposts = repostCount(comments, seed);
        const foot = createDiv('nw-foot');
        const chatItem = createSpan('nw-foot-item');
        chatItem.appendChild(icon('chat' as IconName));
        chatItem.appendChild(createSpan(undefined, ` ${Utils.formatCountCN(comments)}`));
        foot.appendChild(chatItem);
        const repostItem = createSpan('nw-foot-item');
        repostItem.appendChild(icon('repost' as IconName));
        repostItem.appendChild(createSpan(undefined, ` ${Utils.formatCountCN(reposts)}`));
        foot.appendChild(repostItem);
        card.appendChild(foot);

        // 2026-10-16 弹窗统一批（茄子令）：卡内这颗「知道了」删掉，关闭热区＝遮罩本体（见构造函数）
        return card;
    }

    // ===== news 报刊（方案 D） =====

    private renderNewsPaper(media: FixedMedia): HTMLElement {
        const card = createDiv('media-news-paper');

        // 报头
        const masthead = createDiv('d-masthead');
        masthead.appendChild(createDiv('d-name', ['娱乐星闻']));
        const p = GameState.getInstance().player;   // 渲染时现取单例（构造期捕获会在建角换实例后失效）
        const dateStr = `ENTERTAINMENT · 第${p.year + 1}年${p.month}月`;
        masthead.appendChild(createDiv('d-sub', [dateStr]));
        card.appendChild(masthead);

        // 报头双线（实线，不动）
        card.appendChild(createDiv('d-rule'));
        card.appendChild(createDiv('d-rule thin'));

        // 标签
        const tagWrap = createDiv('d-tag-wrap');
        tagWrap.appendChild(createSpan('d-tag', '行业新闻'));
        card.appendChild(tagWrap);

        // 标题（微博无标题不走这里）
        if (media.title) card.appendChild(setPlotText(createDiv('d-title'), media.title));

        // 导语（带左竖条）
        card.appendChild(setPlotText(createDiv('d-lead'), media.body));

        // 署名
        card.appendChild(createDiv('d-byline', ['—— 本刊娱乐部']));

        // 2026-10-16 弹窗统一批（茄子令）：卡内这颗「知道了」删掉，关闭热区＝遮罩本体（见构造函数）
        return card;
    }
}
